import React, { useState, useEffect } from "react";
import { GetData, PostData, PutData, DeleteData } from "../../services/api";
import DataTable from "react-data-table-component";
import {
  DropdownItem,
  DropdownMenu,
  DropdownToggle,
  Input,
  UncontrolledDropdown,
  Container,
  Row,
  Col,
  Button,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Label,
  FormGroup,
  Card,
  CardBody,
  CardHeader,
  Badge,
} from "reactstrap";
import { Link, useParams, Navigate } from "react-router-dom";
import Breadcrumbs from "../../components/Common/Breadcrumb";
import Loader from "../../components/Common/Loader";
import Swal from 'sweetalert2';

const SalesTeamDetails = () => {
  const { id } = useParams();
  const [team, setTeam] = useState(null);
  const [members, setMembers] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [memberModal, setMemberModal] = useState(false);
  const [editMemberModal, setEditMemberModal] = useState(false);
  const [editingMember, setEditingMember] = useState(null);
  const [selectedUserIds, setSelectedUserIds] = useState([]); // Cambiar a array para múltiple selección
  const [assignmentPercentage, setAssignmentPercentage] = useState(0);
  const [userPercentages, setUserPercentages] = useState({}); // Objeto para almacenar porcentaje por usuario
  const [isActive, setIsActive] = useState(true);

  // Check if user is admin
  const getCurrentUserRole = () => {
    try {
      const authUser = localStorage.getItem("authUser");
      if (authUser) {
        const user = JSON.parse(authUser);
        // El rol puede estar en el JWT decodificado o necesitamos obtenerlo del perfil
        return user.role || user.name?.role || "user";
      }
    } catch (error) {
      console.error("Error getting user role:", error);
    }
    return "user";
  };

  const userRole = getCurrentUserRole();
  const isAdmin = userRole === "admin";
  
  // Debug: log para verificar el rol
  console.log("Current user role:", userRole, "isAdmin:", isAdmin);

  useEffect(() => {
    fetchTeam();
    fetchMembers();
    fetchUsers();
  }, [id]);

  const fetchTeam = async () => {
    try {
      const { data } = await GetData(`/sales-teams/${id}`);
      setTeam(data);
      setLoading(false);
    } catch (error) {
      console.error("Error fetching team:", error);
      setLoading(false);
    }
  };

  const fetchMembers = async () => {
    try {
      const { data } = await GetData(`/sales-teams/${id}/members`);
      console.log("[SalesTeamDetails] Fetched members:", data);
      console.log("[SalesTeamDetails] Members count:", data?.length || 0);
      // Normalizar userIds a string para comparación consistente
      const normalizedMembers = (data || []).map(m => {
        const userId = String(m.userId || m.user?.id || m.user?._id || '').trim();
        return {
          ...m,
          userId: userId,
          // Asegurar que user también tenga el ID normalizado si existe
          user: m.user ? {
            ...m.user,
            id: String(m.user.id || m.user._id || '').trim(),
          } : null,
        };
      });
      console.log("[SalesTeamDetails] Normalized members:", normalizedMembers);
      setMembers(normalizedMembers);
    } catch (error) {
      console.error("Error fetching members:", error);
      setMembers([]);
    }
  };

  const fetchUsers = async () => {
    try {
      const { data } = await GetData("/users");
      console.log("[SalesTeamDetails] Fetched users:", data);
      console.log("[SalesTeamDetails] Users count:", data?.length || 0);
      // Normalizar IDs a string para comparación consistente
      const normalizedUsers = (data || []).map(u => ({
        ...u,
        id: String(u.id || u._id || '').trim(),
      }));
      console.log("[SalesTeamDetails] Normalized users:", normalizedUsers);
      setUsers(normalizedUsers);
    } catch (error) {
      console.error("Error fetching users:", error);
      console.error("Error details:", error.response?.data || error.message);
      setUsers([]);
    }
  };

  // Helper function para encontrar usuario por ID
  const findUserById = (userId) => {
    if (!userId) return null;
    const userIdStr = String(userId).trim();
    // Intentar múltiples formas de comparación
    return users.find(u => {
      const uId = String(u.id || u._id || '').trim();
      return uId === userIdStr || uId === String(userId).trim();
    });
  };

  const columns = [
    {
      name: <span className="font-weight-bold fs-13">Usuario</span>,
      selector: (row) => {
        // Primero intentar con row.user si existe
        if (row.user && (row.user.name || row.user.email)) {
          const fullName = [row.user.name, row.user.lastName].filter(Boolean).join(' ').trim();
          return fullName || row.user.email || row.userId || '-';
        }
        // Si no hay user o no tiene datos, buscar en la lista de usuarios
        const user = findUserById(row.userId);
        if (user) {
          const fullName = [user.name, user.lastName].filter(Boolean).join(' ').trim();
          return fullName || user.email || row.userId || '-';
        }
        return row.userId || '-';
      },
      sortable: true,
      cell: (row) => {
        let displayName = '-';
        let email = '-';
        
        // Primero intentar con row.user si existe y tiene datos
        if (row.user && (row.user.name || row.user.email)) {
          const fullName = [row.user.name, row.user.lastName].filter(Boolean).join(' ').trim();
          displayName = fullName || row.user.email || row.userId || '-';
          email = row.user.email || '-';
        } else {
          // Si no hay user o no tiene datos, buscar en la lista de usuarios
          const user = findUserById(row.userId);
          if (user) {
            const fullName = [user.name, user.lastName].filter(Boolean).join(' ').trim();
            displayName = fullName || user.email || row.userId || '-';
            email = user.email || '-';
          } else {
            // Si no se encuentra, mostrar el userId como fallback
            displayName = row.userId || '-';
            email = '-';
            console.warn('[SalesTeamDetails] User not found for member:', {
              memberId: row.id,
              userId: row.userId,
              availableUserIds: users.map(u => u.id).slice(0, 5),
            });
          }
        }
        
        return (
          <div>
            <div className="fw-medium">{displayName}</div>
            <div className="text-muted small">{email}</div>
          </div>
        );
      },
    },
    {
      name: <span className="font-weight-bold fs-13">Porcentaje</span>,
      selector: (row) => `${row.assignmentPercentage || 0}%`,
      sortable: true,
    },
    {
      name: <span className="font-weight-bold fs-13">Leads Asignados</span>,
      selector: (row) => row.totalAssigned || 0,
      sortable: true,
    },
    {
      name: <span className="font-weight-bold fs-13">Estado</span>,
      selector: (row) => row.isActive ? 'Activo' : 'Inactivo',
      sortable: true,
      cell: (row) => (
        <Badge color={row.isActive ? 'success' : 'danger'}>
          {row.isActive ? 'Activo' : 'Inactivo'}
        </Badge>
      ),
    },
    {
      name: <span className="font-weight-bold fs-13">Acción</span>,
      sortable: false,
      cell: (data) => {
        return (
          <UncontrolledDropdown className="dropdown d-inline-block" direction="down">
            <DropdownToggle
              className="btn btn-soft-secondary btn-sm"
              tag="button"
              type="button"
            >
              <i className="ri-more-fill align-middle"></i>
            </DropdownToggle>
            <DropdownMenu 
              className="dropdown-menu-end" 
              style={{ zIndex: 9999 }}
              flip={false}
            >
              <DropdownItem 
                className="edit-item-btn" 
                onClick={() => {
                  setEditingMember(data);
                  setAssignmentPercentage(data.assignmentPercentage || 0);
                  setIsActive(data.isActive ?? true);
                  setEditMemberModal(true);
                }}
              >
                <i className="ri-pencil-fill align-bottom me-2 text-muted"></i>
                Editar
              </DropdownItem>
              <DropdownItem 
                className="remove-item-btn" 
                onClick={() => deleteMember(data.id)}
              >
                <i className="ri-delete-bin-fill align-bottom me-2 text-muted"></i>
                Eliminar
              </DropdownItem>
            </DropdownMenu>
          </UncontrolledDropdown>
        );
      },
    },
  ];

  const createMember = async () => {
    try {
      console.log('[SalesTeamDetails] createMember llamado con:', {
        selectedUserIds,
        selectedUserIdsLength: selectedUserIds.length,
        userPercentages,
        membersCount: members.length,
        members: members.map(m => ({ userId: m.userId, percentage: m.assignmentPercentage, isActive: m.isActive }))
      });

      if (!selectedUserIds || selectedUserIds.length === 0) {
        Swal.fire({
          title: "Error",
          text: "Debes seleccionar al menos un usuario",
          icon: "error"
        });
        return;
      }

      // Asegurar que tenemos los miembros más recientes antes de validar
      // Esto previene problemas de caché
      let currentMembers = members;
      try {
        const { data } = await GetData(`/sales-teams/${id}/members`);
        if (data && Array.isArray(data)) {
          currentMembers = data.map(m => ({
            ...m,
            userId: String(m.userId || m.user?.id || m.user?._id || '').trim(),
          }));
          console.log('[SalesTeamDetails] Miembros actualizados desde el servidor:', currentMembers.length);
        }
      } catch (error) {
        console.warn('[SalesTeamDetails] No se pudieron obtener miembros actualizados, usando estado local:', error);
      }

      // Validar porcentajes individuales si es estrategia de porcentajes
      if (team.assignmentStrategy === 'percentage') {
        const invalidUsers = selectedUserIds.filter(userId => {
          const percentage = userPercentages[userId] || 0;
          return percentage <= 0 || percentage > 100;
        });
        
        if (invalidUsers.length > 0) {
          Swal.fire({
            title: "Error",
            text: "Todos los usuarios deben tener un porcentaje válido (mayor a 0 y menor o igual a 100)",
            icon: "error"
          });
          return;
        }

        // Validar que la suma de porcentajes sea 100% antes de crear cualquier miembro
        // Solo considerar miembros activos (usar currentMembers que se obtuvo del servidor)
        const activeMembers = currentMembers.filter(m => m.isActive !== false);
        const currentMembersTotal = activeMembers.reduce((sum, m) => {
          const percentage = m.assignmentPercentage || 0;
          return sum + percentage;
        }, 0);
        
        const newMembersTotal = selectedUserIds.reduce((sum, userId) => {
          return sum + (userPercentages[userId] || 0);
        }, 0);
        
        const totalAfterAdd = currentMembersTotal + newMembersTotal;

        console.log('[SalesTeamDetails] Validación de porcentajes:', {
          activeMembersCount: activeMembers.length,
          currentMembersTotal,
          newMembersTotal,
          totalAfterAdd,
          members: activeMembers.map(m => ({ userId: m.userId, percentage: m.assignmentPercentage, isActive: m.isActive }))
        });

        if (Math.abs(totalAfterAdd - 100) > 0.01) {
          Swal.fire({
            title: "Error",
            text: `La suma de porcentajes debe ser exactamente 100%. Actualmente tienes ${currentMembersTotal.toFixed(2)}% y con estos nuevos miembros sería ${totalAfterAdd.toFixed(2)}%`,
            icon: "error"
          });
          return;
        }
      }

      // Si hay múltiples miembros, usar endpoint batch para validar todos juntos
      // Si solo hay uno, usar el endpoint individual
      if (selectedUserIds.length > 1) {
        // Usar endpoint batch para crear todos los miembros de una vez
        console.log('[SalesTeamDetails] Usando endpoint batch para crear múltiples miembros:', {
          total: selectedUserIds.length,
          userIds: selectedUserIds,
          percentages: selectedUserIds.map(userId => ({ userId, percentage: userPercentages[userId] || 0 }))
        });

        try {
          const membersData = selectedUserIds.map(userId => ({
            userId: userId,
            assignmentPercentage: team.assignmentStrategy === 'percentage' ? (userPercentages[userId] || 0) : 0,
            isActive: isActive,
          }));

          const result = await PostData(`/sales-teams/${id}/members/batch`, membersData);
          console.log(`[SalesTeamDetails] ${result.length} miembros creados exitosamente mediante batch`);

          Swal.fire({
            title: "¡Éxito!",
            text: `${result.length} miembro(s) agregado(s) exitosamente`,
            icon: "success"
          });
          
          fetchMembers();
          setMemberModal(false);
          resetMemberForm();
        } catch (error) {
          const errorMessage = error.response?.data?.error?.message || error.message || 'Error desconocido';
          console.error(`[SalesTeamDetails] Error al agregar miembros mediante batch:`, {
            errorMessage,
            errorResponse: error.response?.data,
            fullError: error
          });

          Swal.fire({
            title: "Error",
            text: errorMessage,
            icon: "error"
          });
          fetchMembers();
        }
      } else {
        // Crear un solo miembro usando el endpoint individual
        const userId = selectedUserIds[0];
        const percentage = team.assignmentStrategy === 'percentage' ? (userPercentages[userId] || 0) : 0;
        
        console.log(`[SalesTeamDetails] Creando miembro único ${userId} con porcentaje ${percentage}%`);

        try {
          const result = await PostData(`/sales-teams/${id}/members`, {
            userId: userId,
            assignmentPercentage: percentage,
            isActive: isActive,
          });
          console.log(`[SalesTeamDetails] Miembro ${userId} creado exitosamente`);

          Swal.fire({
            title: "¡Éxito!",
            text: "Miembro agregado exitosamente",
            icon: "success"
          });
          
          fetchMembers();
          setMemberModal(false);
          resetMemberForm();
        } catch (error) {
          const errorMessage = error.response?.data?.error?.message || error.message || 'Error desconocido';
          console.error(`[SalesTeamDetails] Error al agregar miembro:`, {
            errorMessage,
            errorResponse: error.response?.data,
            fullError: error
          });

          Swal.fire({
            title: "Error",
            text: errorMessage,
            icon: "error"
          });
          fetchMembers();
        }
      }
    } catch (err) {
      console.log(err);
      Swal.fire({
        title: "Error",
        text: err.response?.data?.error?.message || err.message || "Error al agregar los miembros",
        icon: "error"
      });
    }
  };

  const toggleUserSelection = (userId) => {
    const userIdStr = String(userId).trim();
    console.log("[SalesTeamDetails] toggleUserSelection called with:", userIdStr);
    console.log("[SalesTeamDetails] Current selectedUserIds:", selectedUserIds);
    console.log("[SalesTeamDetails] Current selectedUserIds types:", selectedUserIds.map(id => `${id} (${typeof id})`));
    
    setSelectedUserIds(prev => {
      // Normalize all IDs to strings for comparison
      const normalizedPrev = prev.map(id => String(id).trim());
      const isSelected = normalizedPrev.includes(userIdStr);
      
      console.log("[SalesTeamDetails] Normalized prev:", normalizedPrev);
      console.log("[SalesTeamDetails] Is user selected?", isSelected);
      
      if (isSelected) {
        const newSelection = normalizedPrev.filter(id => id !== userIdStr);
        // Remover el porcentaje del usuario deseleccionado
        setUserPercentages(prev => {
          const newPercentages = { ...prev };
          delete newPercentages[userIdStr];
          return newPercentages;
        });
        console.log("[SalesTeamDetails] Removing user, new selection:", newSelection);
        return newSelection;
      } else {
        const newSelection = [...normalizedPrev, userIdStr];
        // Inicializar porcentaje en 0 para el nuevo usuario
        setUserPercentages(prev => ({
          ...prev,
          [userIdStr]: 0
        }));
        console.log("[SalesTeamDetails] Adding user, new selection:", newSelection);
        return newSelection;
      }
    });
  };

  const selectAllUsers = () => {
    const memberUserIds = members.map(m => String(m.userId || ''));
    const availableUsers = users.filter(u => !memberUserIds.includes(String(u.id || '')));
    const newSelectedIds = availableUsers.map(u => String(u.id || ''));
    setSelectedUserIds(newSelectedIds);
    // Inicializar porcentajes en 0 para todos los usuarios seleccionados
    const newPercentages = {};
    newSelectedIds.forEach(userId => {
      newPercentages[userId] = 0;
    });
    setUserPercentages(newPercentages);
  };

  const deselectAllUsers = () => {
    setSelectedUserIds([]);
    setUserPercentages({});
  };

  const updateMember = async () => {
    try {
      if (team.assignmentStrategy === 'percentage' && assignmentPercentage <= 0) {
        Swal.fire({
          title: "Error",
          text: "El porcentaje debe ser mayor a 0",
          icon: "error"
        });
        return;
      }

      const updatePayload = {
        assignmentPercentage: team.assignmentStrategy === 'percentage' ? assignmentPercentage : 0,
        isActive: isActive,
      };

      console.log('[SalesTeamDetails] Updating member:', {
        memberId: editingMember.id,
        payload: updatePayload,
        isActiveType: typeof isActive,
        isActiveValue: isActive,
      });

      const response = await PutData(`/sales-teams/${id}/members/${editingMember.id}`, updatePayload);

      console.log('[SalesTeamDetails] Update response:', response);

      if (response?.status === 204 || response?.status === 200) {
        Swal.fire({
          title: "Miembro actualizado",
          icon: "success"
        });
        fetchMembers();
        setEditMemberModal(false);
        setEditingMember(null);
        resetMemberForm();
      }
    } catch (err) {
      console.error('[SalesTeamDetails] Error updating member:', err);
      console.error('[SalesTeamDetails] Error response:', err.response?.data);
      Swal.fire({
        title: "Error",
        text: err.response?.data?.error?.message || err.message || "Error al actualizar el miembro",
        icon: "error"
      });
    }
  };

  const deleteMember = (memberId) => {
    Swal.fire({
      title: "¿Deseas eliminar el miembro?",
      showCancelButton: true,
      confirmButtonText: "Eliminar",
      cancelButtonText: "Cancelar",
      confirmButtonColor: '#dc3545'
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          await DeleteData(`/sales-teams/${id}/members/${memberId}`);
          Swal.fire("Miembro eliminado!", "", "success");
          fetchMembers();
        } catch (err) {
          Swal.fire({
            title: "Error",
            text: err.response?.data?.error?.message || err.message || "Error al eliminar el miembro",
            icon: "error"
          });
        }
      }
    });
  };

  const resetMemberForm = () => {
    setSelectedUserIds([]);
    setAssignmentPercentage(0);
    setUserPercentages({});
    setIsActive(true);
  };

  const totalPercentage = members.reduce((sum, m) => sum + (m.assignmentPercentage || 0), 0);
  const totalAssigned = members.reduce((sum, m) => sum + (m.totalAssigned || 0), 0);

  // Redirect if not admin
  if (!isAdmin) {
    return <Navigate to="/dashboard" replace />;
  }

  if (loading) {
    return (
      <div className="page-content">
        <Container fluid={true}>
          <Loader size={150} />
        </Container>
      </div>
    );
  }

  return (
    <div className="page-content">
      <Container fluid={true}>
        <Breadcrumbs 
          title="Ventas" 
          breadcrumbItem={
            <>
              <Link to="/sales-teams">Equipos de Venta</Link> / {team?.name || "Detalles"}
            </>
          } 
        />

        {/* Información del Equipo */}
        <Row className="mb-4">
          <Col>
            <Card>
              <CardHeader>
                <h5 className="card-title mb-0">Información del Equipo</h5>
              </CardHeader>
              <CardBody>
                <Row>
                  <Col md={6}>
                    <p><strong>Nombre:</strong> {team?.name}</p>
                    <p><strong>Descripción:</strong> {team?.description || "Sin descripción"}</p>
                  </Col>
                  <Col md={6}>
                    <p><strong>Estrategia:</strong> {team?.assignmentStrategy === 'percentage' ? 'Por Porcentajes' : 'Round Robin'}</p>
                    <p><strong>Estado:</strong> 
                      <Badge color={team?.isActive ? 'success' : 'danger'} className="ms-2">
                        {team?.isActive ? 'Activo' : 'Inactivo'}
                      </Badge>
                    </p>
                  </Col>
                </Row>
              </CardBody>
            </Card>
          </Col>
        </Row>

        {/* Estadísticas */}
        {team?.assignmentStrategy === 'percentage' && (
          <Row className="mb-4">
            <Col md={4}>
              <Card>
                <CardBody>
                  <h6 className="text-muted mb-2">Total Porcentajes</h6>
                  <h3>{totalPercentage}%</h3>
                </CardBody>
              </Card>
            </Col>
            <Col md={4}>
              <Card>
                <CardBody>
                  <h6 className="text-muted mb-2">Total Leads Asignados</h6>
                  <h3>{totalAssigned}</h3>
                </CardBody>
              </Card>
            </Col>
            <Col md={4}>
              <Card>
                <CardBody>
                  <h6 className="text-muted mb-2">Miembros Activos</h6>
                  <h3>{members.filter(m => m.isActive).length}</h3>
                </CardBody>
              </Card>
            </Col>
          </Row>
        )}

        {/* Miembros */}
        <Row className="mb-4">
          <Col>
            <Card>
              <CardHeader className="d-flex justify-content-between align-items-center">
                <h5 className="card-title mb-0">Miembros del Equipo</h5>
                <Button
                  color="success"
                  size="sm"
                  onClick={() => {
                    resetMemberForm();
                    setMemberModal(true);
                  }}
                >
                  <i className="ri-add-line align-bottom me-1"></i> Agregar Miembro
                </Button>
              </CardHeader>
              <CardBody style={{ position: 'relative', overflow: 'visible' }}>
                <style>{`
                  .rdt_Table {
                    overflow: visible !important;
                  }
                  .rdt_TableBody {
                    overflow: visible !important;
                    min-height: ${members.length <= 3 ? '400px' : 'auto'} !important;
                  }
                  .rdt_TableCell {
                    overflow: visible !important;
                  }
                  .rdt_TableRow {
                    overflow: visible !important;
                  }
                  .rdt_TableWrapper {
                    overflow: visible !important;
                  }
                `}</style>
                <DataTable
                  data={members}
                  columns={columns}
                  noDataComponent={<span className="py-4">Sin miembros en este equipo</span>}
                />
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>

      {/* Modal Agregar Miembro */}
      <Modal
        isOpen={memberModal}
        centered
        toggle={() => {
          setMemberModal(false);
          resetMemberForm();
        }}
      >
        <ModalHeader
          className="bg-light p-3"
          id="exampleModalLabel"
        >
          Agregar Miembro al Equipo
        </ModalHeader>
        <div className="tablelist-form">
          <ModalBody style={{ height: "auto", maxHeight: "70vh", overflowY: "auto" }}>
            <div className="mb-3">
              <div className="d-flex justify-content-between align-items-center mb-2">
                <label className="form-label mb-0">
                  Usuarios <span className="text-danger">*</span>
                  <small className="text-muted ms-2">
                    ({selectedUserIds.length} seleccionado{selectedUserIds.length !== 1 ? 's' : ''})
                  </small>
                </label>
                <div>
                  <Button
                    size="sm"
                    color="link"
                    className="p-0 me-2"
                    onClick={selectAllUsers}
                  >
                    Seleccionar todos
                  </Button>
                  <Button
                    size="sm"
                    color="link"
                    className="p-0"
                    onClick={deselectAllUsers}
                  >
                    Deseleccionar todos
                  </Button>
                </div>
              </div>
              <div style={{ 
                border: "1px solid #dee2e6", 
                borderRadius: "0.25rem", 
                padding: "10px",
                maxHeight: "300px",
                overflowY: "auto"
              }}>
                {(() => {
                  // Convertir todos los IDs a string para comparación consistente
                  const memberUserIds = members.map(m => String(m.userId || ''));
                  const availableUsers = users.filter(u => {
                    const userId = String(u.id || '');
                    return !memberUserIds.includes(userId);
                  });
                  
                  console.log("[SalesTeamDetails] Available users:", availableUsers.length, "of", users.length);
                  console.log("[SalesTeamDetails] Member user IDs:", memberUserIds);
                  console.log("[SalesTeamDetails] All user IDs:", users.map(u => String(u.id || '')));
                  
                  if (availableUsers.length === 0) {
                    return <p className="text-muted mb-0">No hay usuarios disponibles para agregar</p>;
                  }
                  
                  return availableUsers.map(user => {
                    const userIdStr = String(user.id || '').trim();
                    // Normalize selectedUserIds for comparison
                    const normalizedSelected = selectedUserIds.map(id => String(id).trim());
                    const isSelected = normalizedSelected.includes(userIdStr);
                    
                    return (
                      <div key={userIdStr} className="mb-2">
                        <FormGroup check>
                          <Input
                            type="checkbox"
                            id={`user-${userIdStr}`}
                            checked={isSelected}
                            onChange={(e) => {
                              e.stopPropagation();
                              console.log(`[SalesTeamDetails] Checkbox onChange for user ${userIdStr}, event checked: ${e.target.checked}, current isSelected: ${isSelected}`);
                              toggleUserSelection(userIdStr);
                            }}
                            onClick={(e) => {
                              e.stopPropagation();
                              console.log(`[SalesTeamDetails] Checkbox onClick for user ${userIdStr}`);
                            }}
                          />
                          <Label 
                            check 
                            htmlFor={`user-${userIdStr}`}
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              console.log(`[SalesTeamDetails] Label onClick for user ${userIdStr}`);
                              toggleUserSelection(userIdStr);
                            }}
                            style={{ cursor: 'pointer' }}
                          >
                            {user.name || user.email} ({user.email})
                          </Label>
                        </FormGroup>
                      </div>
                    );
                  });
                })()}
              </div>
            </div>
            {team?.assignmentStrategy === 'percentage' && selectedUserIds.length > 0 && (
              <div className="mb-3">
                <label className="form-label">
                  Porcentaje de Asignación por Usuario <span className="text-danger">*</span>
                </label>
                <div style={{ 
                  border: "1px solid #dee2e6", 
                  borderRadius: "0.25rem", 
                  padding: "10px",
                  maxHeight: "200px",
                  overflowY: "auto"
                }}>
                  {selectedUserIds.map(userId => {
                    const user = users.find(u => String(u.id || '') === userId);
                    const displayName = user ? (user.name || user.email || userId) : userId;
                    return (
                      <div key={userId} className="mb-2">
                        <div className="d-flex align-items-center justify-content-between">
                          <label className="form-label mb-0 me-2" style={{ minWidth: '150px' }}>
                            {displayName}:
                          </label>
                          <Input
                            type="number"
                            min="0"
                            max="100"
                            value={userPercentages[userId] || 0}
                            onChange={(e) => {
                              const value = parseInt(e.target.value) || 0;
                              setUserPercentages(prev => ({
                                ...prev,
                                [userId]: value
                              }));
                            }}
                            style={{ width: '100px' }}
                          />
                          <span className="ms-2">%</span>
                        </div>
                      </div>
                    );
                  })}
                </div>
                <small className="text-muted">
                  Asigna un porcentaje individual a cada usuario seleccionado (0-100)
                </small>
              </div>
            )}
            <FormGroup check className="mt-3">
              <Input
                type="checkbox"
                id="member-isActive"
                checked={isActive}
                onChange={(e) => setIsActive(e.target.checked)}
              />
              <Label check htmlFor="member-isActive">
                Miembro Activo
              </Label>
            </FormGroup>
          </ModalBody>
          <ModalFooter>
            <div className="">
              <button
                type="button"
                className="btn btn-light mx-4"
                onClick={() => {
                  setMemberModal(false);
                  resetMemberForm();
                }}
              >
                Cerrar
              </button>
              <button
                onClick={createMember}
                className="btn btn-success"
                disabled={selectedUserIds.length === 0 || (team?.assignmentStrategy === 'percentage' && selectedUserIds.some(userId => (userPercentages[userId] || 0) <= 0))}
              >
                Agregar {selectedUserIds.length > 0 ? `(${selectedUserIds.length})` : ''}
              </button>
            </div>
          </ModalFooter>
        </div>
      </Modal>

      {/* Modal Editar Miembro */}
      <Modal
        isOpen={editMemberModal}
        centered
        toggle={() => {
          setEditMemberModal(false);
          setEditingMember(null);
          resetMemberForm();
        }}
      >
        <ModalHeader
          className="bg-light p-3"
          id="exampleModalLabel"
        >
          Editar Miembro
        </ModalHeader>
        <div className="tablelist-form">
          <ModalBody style={{ height: "auto" }}>
            {team?.assignmentStrategy === 'percentage' && (
              <div className="mb-3">
                <label htmlFor="edit-percentage-field" className="form-label">
                  Porcentaje de Asignación <span className="text-danger">*</span>
                </label>
                <Input
                  type="number"
                  id="edit-percentage-field"
                  min="0"
                  max="100"
                  value={assignmentPercentage}
                  onChange={(e) => setAssignmentPercentage(parseInt(e.target.value) || 0)}
                />
                <small className="text-muted">Porcentaje de leads que recibirá este miembro (0-100)</small>
              </div>
            )}
            <FormGroup check className="mt-3">
              <Input
                type="checkbox"
                id="edit-member-isActive"
                checked={isActive}
                onChange={(e) => {
                  const newValue = e.target.checked;
                  console.log('[SalesTeamDetails] Checkbox onChange:', {
                    checked: newValue,
                    previousValue: isActive,
                    memberId: editingMember?.id,
                  });
                  setIsActive(newValue);
                }}
              />
              <Label 
                check 
                htmlFor="edit-member-isActive"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  const newValue = !isActive;
                  console.log('[SalesTeamDetails] Label onClick:', {
                    checked: newValue,
                    previousValue: isActive,
                    memberId: editingMember?.id,
                  });
                  setIsActive(newValue);
                }}
                style={{ cursor: 'pointer' }}
              >
                Miembro Activo
              </Label>
            </FormGroup>
          </ModalBody>
          <ModalFooter>
            <div className="">
              <button
                type="button"
                className="btn btn-light mx-4"
                onClick={() => {
                  setEditMemberModal(false);
                  setEditingMember(null);
                  resetMemberForm();
                }}
              >
                Cerrar
              </button>
              <button
                onClick={updateMember}
                className="btn btn-success"
                disabled={team?.assignmentStrategy === 'percentage' && assignmentPercentage <= 0}
              >
                Actualizar
              </button>
            </div>
          </ModalFooter>
        </div>
      </Modal>
    </div>
  );
};

export default SalesTeamDetails;

